import * as z from 'zod';

export const CreateLocationRequest = z.object({
  location: z
    .object({
      _id: z
        .string()
        .describe('Location ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Location name.').max(150),
      description: z
        .string()
        .describe('Location description.')
        .max(500)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
        )
        .optional(),
      status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
      locationType: z
        .enum([
          'UNKNOWN',
          'BRANCH',
          'OFFICES',
          'RECEPTION',
          'HEADQUARTERS',
          'INVENTORY',
        ])
        .optional(),
      fax: z.string().describe('Fax number.').optional().nullable(),
      timeZone: z.string().describe('Timezone in `America/New_York` format.'),
      email: z.string().describe('Email address.').optional().nullable(),
      phone: z.string().describe('Phone number.').optional().nullable(),
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
        })
        .describe('Address.'),
      businessSchedule: z
        .object({
          periods: z
            .array(
              z.object({
                openDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .optional(),
                openTime: z
                  .string()
                  .describe(
                    'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
                  )
                  .optional(),
                closeDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .optional(),
                closeTime: z
                  .string()
                  .describe(
                    'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
          specialHourPeriod: z
            .array(
              z.object({
                startDate: z
                  .string()
                  .describe(
                    'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                endDate: z
                  .string()
                  .describe(
                    'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                isClosed: z
                  .boolean()
                  .describe(
                    'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
                  )
                  .optional(),
                comment: z
                  .string()
                  .describe(
                    'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
        })
        .describe(
          'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
        )
        .optional(),
      locationTypes: z
        .array(
          z.enum([
            'UNKNOWN',
            'BRANCH',
            'OFFICES',
            'RECEPTION',
            'HEADQUARTERS',
            'INVENTORY',
          ])
        )
        .max(10)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields for data extensions.')
        .optional(),
    })
    .describe('Location to create.'),
});
export const CreateLocationResponse = z.object({
  _id: z
    .string()
    .describe('Location ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  name: z.string().describe('Location name.').max(150).optional(),
  description: z
    .string()
    .describe('Location description.')
    .max(500)
    .optional()
    .nullable(),
  default: z
    .boolean()
    .describe(
      "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
    )
    .optional(),
  status: z
    .enum(['ACTIVE', 'INACTIVE'])
    .describe(
      "Location status. Defaults to `ACTIVE`.\n__Notes:__\n- [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect the location's status.\n- `INACTIVE` status is currently not supported."
    )
    .optional(),
  locationType: z
    .enum([
      'UNKNOWN',
      'BRANCH',
      'OFFICES',
      'RECEPTION',
      'HEADQUARTERS',
      'INVENTORY',
    ])
    .describe('Location type.\n\n**Note:** Currently not supported.')
    .optional(),
  fax: z.string().describe('Fax number.').optional().nullable(),
  timeZone: z
    .string()
    .describe('Timezone in `America/New_York` format.')
    .optional()
    .nullable(),
  email: z.string().describe('Email address.').optional().nullable(),
  phone: z.string().describe('Phone number.').optional().nullable(),
  address: z
    .object({
      streetAddress: z
        .object({ name: z.string().optional(), number: z.string().optional() })
        .optional(),
      city: z.string().optional().nullable(),
      subdivision: z.string().optional().nullable(),
      country: z.string().optional().nullable(),
      postalCode: z.string().optional().nullable(),
    })
    .describe('Address.')
    .optional(),
  businessSchedule: z
    .object({
      periods: z
        .array(
          z.object({
            openDay: z
              .enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
              .describe('Day of the week the period starts on.')
              .optional(),
            openTime: z
              .string()
              .describe(
                'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
              )
              .optional(),
            closeDay: z
              .enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
              .describe('Day of the week the period ends on.')
              .optional(),
            closeTime: z
              .string()
              .describe(
                'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
              )
              .optional(),
          })
        )
        .max(100)
        .optional(),
      specialHourPeriod: z
        .array(
          z.object({
            startDate: z
              .string()
              .describe(
                'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
              )
              .optional(),
            endDate: z
              .string()
              .describe(
                'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
              )
              .optional(),
            isClosed: z
              .boolean()
              .describe(
                'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
              )
              .optional(),
            comment: z
              .string()
              .describe(
                'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
              )
              .optional(),
          })
        )
        .max(100)
        .optional(),
    })
    .describe(
      'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
    )
    .optional()
    .nullable(),
  archived: z
    .boolean()
    .describe(
      "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
    )
    .optional(),
  locationTypes: z
    .array(
      z.enum([
        'UNKNOWN',
        'BRANCH',
        'OFFICES',
        'RECEPTION',
        'HEADQUARTERS',
        'INVENTORY',
      ])
    )
    .max(10)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields for data extensions.')
    .optional(),
});
export const GetLocationRequest = z.object({
  _id: z
    .string()
    .describe('ID of the location to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetLocationResponse = z.object({
  _id: z
    .string()
    .describe('Location ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  name: z.string().describe('Location name.').max(150).optional(),
  description: z
    .string()
    .describe('Location description.')
    .max(500)
    .optional()
    .nullable(),
  default: z
    .boolean()
    .describe(
      "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
    )
    .optional(),
  status: z
    .enum(['ACTIVE', 'INACTIVE'])
    .describe(
      "Location status. Defaults to `ACTIVE`.\n__Notes:__\n- [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect the location's status.\n- `INACTIVE` status is currently not supported."
    )
    .optional(),
  locationType: z
    .enum([
      'UNKNOWN',
      'BRANCH',
      'OFFICES',
      'RECEPTION',
      'HEADQUARTERS',
      'INVENTORY',
    ])
    .describe('Location type.\n\n**Note:** Currently not supported.')
    .optional(),
  fax: z.string().describe('Fax number.').optional().nullable(),
  timeZone: z
    .string()
    .describe('Timezone in `America/New_York` format.')
    .optional()
    .nullable(),
  email: z.string().describe('Email address.').optional().nullable(),
  phone: z.string().describe('Phone number.').optional().nullable(),
  address: z
    .object({
      streetAddress: z
        .object({ name: z.string().optional(), number: z.string().optional() })
        .optional(),
      city: z.string().optional().nullable(),
      subdivision: z.string().optional().nullable(),
      country: z.string().optional().nullable(),
      postalCode: z.string().optional().nullable(),
    })
    .describe('Address.')
    .optional(),
  businessSchedule: z
    .object({
      periods: z
        .array(
          z.object({
            openDay: z
              .enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
              .describe('Day of the week the period starts on.')
              .optional(),
            openTime: z
              .string()
              .describe(
                'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
              )
              .optional(),
            closeDay: z
              .enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
              .describe('Day of the week the period ends on.')
              .optional(),
            closeTime: z
              .string()
              .describe(
                'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
              )
              .optional(),
          })
        )
        .max(100)
        .optional(),
      specialHourPeriod: z
        .array(
          z.object({
            startDate: z
              .string()
              .describe(
                'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
              )
              .optional(),
            endDate: z
              .string()
              .describe(
                'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
              )
              .optional(),
            isClosed: z
              .boolean()
              .describe(
                'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
              )
              .optional(),
            comment: z
              .string()
              .describe(
                'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
              )
              .optional(),
          })
        )
        .max(100)
        .optional(),
    })
    .describe(
      'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
    )
    .optional()
    .nullable(),
  archived: z
    .boolean()
    .describe(
      "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
    )
    .optional(),
  locationTypes: z
    .array(
      z.enum([
        'UNKNOWN',
        'BRANCH',
        'OFFICES',
        'RECEPTION',
        'HEADQUARTERS',
        'INVENTORY',
      ])
    )
    .max(10)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields for data extensions.')
    .optional(),
});
export const ListLocationsRequest = z.object({
  options: z
    .object({
      sort: z
        .object({
          fieldName: z
            .string()
            .describe('Name of the field to sort by.')
            .max(512)
            .optional(),
          order: z.enum(['ASC', 'DESC']).optional(),
        })
        .describe('Sort order.')
        .optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe(
          'Pagination.\n\nDefault values:\n`offset`: 0\n`limit`: 50 (Max: 1000)'
        )
        .optional(),
      includeArchived: z
        .boolean()
        .describe(
          'Whether to include `archived` locations in the response.\n\nDefault: `false`'
        )
        .optional(),
      filterAuthorizedLocationEntities: z
        .boolean()
        .describe('Whether to filter only authorized locations')
        .optional(),
    })
    .describe('Options to use when retrieving a list of locations.')
    .optional(),
});
export const ListLocationsResponse = z.object({
  locations: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Location ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z.string().describe('Location name.').max(150).optional(),
        description: z
          .string()
          .describe('Location description.')
          .max(500)
          .optional()
          .nullable(),
        default: z
          .boolean()
          .describe(
            "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
          )
          .optional(),
        status: z
          .enum(['ACTIVE', 'INACTIVE'])
          .describe(
            "Location status. Defaults to `ACTIVE`.\n__Notes:__\n- [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect the location's status.\n- `INACTIVE` status is currently not supported."
          )
          .optional(),
        locationType: z
          .enum([
            'UNKNOWN',
            'BRANCH',
            'OFFICES',
            'RECEPTION',
            'HEADQUARTERS',
            'INVENTORY',
          ])
          .describe('Location type.\n\n**Note:** Currently not supported.')
          .optional(),
        fax: z.string().describe('Fax number.').optional().nullable(),
        timeZone: z
          .string()
          .describe('Timezone in `America/New_York` format.')
          .optional()
          .nullable(),
        email: z.string().describe('Email address.').optional().nullable(),
        phone: z.string().describe('Phone number.').optional().nullable(),
        address: z
          .object({
            streetAddress: z
              .object({
                name: z.string().optional(),
                number: z.string().optional(),
              })
              .optional(),
            city: z.string().optional().nullable(),
            subdivision: z.string().optional().nullable(),
            country: z.string().optional().nullable(),
            postalCode: z.string().optional().nullable(),
          })
          .describe('Address.')
          .optional(),
        businessSchedule: z
          .object({
            periods: z
              .array(
                z.object({
                  openDay: z
                    .enum([
                      'MONDAY',
                      'TUESDAY',
                      'WEDNESDAY',
                      'THURSDAY',
                      'FRIDAY',
                      'SATURDAY',
                      'SUNDAY',
                    ])
                    .describe('Day of the week the period starts on.')
                    .optional(),
                  openTime: z
                    .string()
                    .describe(
                      'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
                    )
                    .optional(),
                  closeDay: z
                    .enum([
                      'MONDAY',
                      'TUESDAY',
                      'WEDNESDAY',
                      'THURSDAY',
                      'FRIDAY',
                      'SATURDAY',
                      'SUNDAY',
                    ])
                    .describe('Day of the week the period ends on.')
                    .optional(),
                  closeTime: z
                    .string()
                    .describe(
                      'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                    )
                    .optional(),
                })
              )
              .max(100)
              .optional(),
            specialHourPeriod: z
              .array(
                z.object({
                  startDate: z
                    .string()
                    .describe(
                      'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                    )
                    .optional(),
                  endDate: z
                    .string()
                    .describe(
                      'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                    )
                    .optional(),
                  isClosed: z
                    .boolean()
                    .describe(
                      'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
                    )
                    .optional(),
                  comment: z
                    .string()
                    .describe(
                      'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
                    )
                    .optional(),
                })
              )
              .max(100)
              .optional(),
          })
          .describe(
            'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
          )
          .optional()
          .nullable(),
        archived: z
          .boolean()
          .describe(
            "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
          )
          .optional(),
        locationTypes: z
          .array(
            z.enum([
              'UNKNOWN',
              'BRANCH',
              'OFFICES',
              'RECEPTION',
              'HEADQUARTERS',
              'INVENTORY',
            ])
          )
          .max(10)
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields for data extensions.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging info.')
    .optional(),
  authorizedLocationEntities: z.array(z.string()).min(0).max(100).optional(),
});
export const UpdateLocationRequest = z.object({
  _id: z
    .string()
    .describe('Location ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  location: z
    .object({
      _id: z
        .string()
        .describe('Location ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Location name.').max(150),
      description: z
        .string()
        .describe('Location description.')
        .max(500)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
        )
        .optional(),
      status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
      locationType: z
        .enum([
          'UNKNOWN',
          'BRANCH',
          'OFFICES',
          'RECEPTION',
          'HEADQUARTERS',
          'INVENTORY',
        ])
        .optional(),
      fax: z.string().describe('Fax number.').optional().nullable(),
      timeZone: z.string().describe('Timezone in `America/New_York` format.'),
      email: z.string().describe('Email address.').optional().nullable(),
      phone: z.string().describe('Phone number.').optional().nullable(),
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
        })
        .describe('Address.'),
      businessSchedule: z
        .object({
          periods: z
            .array(
              z.object({
                openDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .optional(),
                openTime: z
                  .string()
                  .describe(
                    'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
                  )
                  .optional(),
                closeDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .optional(),
                closeTime: z
                  .string()
                  .describe(
                    'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
          specialHourPeriod: z
            .array(
              z.object({
                startDate: z
                  .string()
                  .describe(
                    'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                endDate: z
                  .string()
                  .describe(
                    'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                isClosed: z
                  .boolean()
                  .describe(
                    'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
                  )
                  .optional(),
                comment: z
                  .string()
                  .describe(
                    'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
        })
        .describe(
          'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
        )
        .optional(),
      locationTypes: z
        .array(
          z.enum([
            'UNKNOWN',
            'BRANCH',
            'OFFICES',
            'RECEPTION',
            'HEADQUARTERS',
            'INVENTORY',
          ])
        )
        .max(10)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields for data extensions.')
        .optional(),
    })
    .describe('Updated location details.'),
});
export const UpdateLocationResponse = z.object({
  _id: z
    .string()
    .describe('Location ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  name: z.string().describe('Location name.').max(150).optional(),
  description: z
    .string()
    .describe('Location description.')
    .max(500)
    .optional()
    .nullable(),
  default: z
    .boolean()
    .describe(
      "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
    )
    .optional(),
  status: z
    .enum(['ACTIVE', 'INACTIVE'])
    .describe(
      "Location status. Defaults to `ACTIVE`.\n__Notes:__\n- [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect the location's status.\n- `INACTIVE` status is currently not supported."
    )
    .optional(),
  locationType: z
    .enum([
      'UNKNOWN',
      'BRANCH',
      'OFFICES',
      'RECEPTION',
      'HEADQUARTERS',
      'INVENTORY',
    ])
    .describe('Location type.\n\n**Note:** Currently not supported.')
    .optional(),
  fax: z.string().describe('Fax number.').optional().nullable(),
  timeZone: z
    .string()
    .describe('Timezone in `America/New_York` format.')
    .optional()
    .nullable(),
  email: z.string().describe('Email address.').optional().nullable(),
  phone: z.string().describe('Phone number.').optional().nullable(),
  address: z
    .object({
      streetAddress: z
        .object({ name: z.string().optional(), number: z.string().optional() })
        .optional(),
      city: z.string().optional().nullable(),
      subdivision: z.string().optional().nullable(),
      country: z.string().optional().nullable(),
      postalCode: z.string().optional().nullable(),
    })
    .describe('Address.')
    .optional(),
  businessSchedule: z
    .object({
      periods: z
        .array(
          z.object({
            openDay: z
              .enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
              .describe('Day of the week the period starts on.')
              .optional(),
            openTime: z
              .string()
              .describe(
                'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
              )
              .optional(),
            closeDay: z
              .enum([
                'MONDAY',
                'TUESDAY',
                'WEDNESDAY',
                'THURSDAY',
                'FRIDAY',
                'SATURDAY',
                'SUNDAY',
              ])
              .describe('Day of the week the period ends on.')
              .optional(),
            closeTime: z
              .string()
              .describe(
                'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
              )
              .optional(),
          })
        )
        .max(100)
        .optional(),
      specialHourPeriod: z
        .array(
          z.object({
            startDate: z
              .string()
              .describe(
                'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
              )
              .optional(),
            endDate: z
              .string()
              .describe(
                'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
              )
              .optional(),
            isClosed: z
              .boolean()
              .describe(
                'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
              )
              .optional(),
            comment: z
              .string()
              .describe(
                'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
              )
              .optional(),
          })
        )
        .max(100)
        .optional(),
    })
    .describe(
      'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
    )
    .optional()
    .nullable(),
  archived: z
    .boolean()
    .describe(
      "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
    )
    .optional(),
  locationTypes: z
    .array(
      z.enum([
        'UNKNOWN',
        'BRANCH',
        'OFFICES',
        'RECEPTION',
        'HEADQUARTERS',
        'INVENTORY',
      ])
    )
    .max(10)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields for data extensions.')
    .optional(),
});
export const QueryLocationsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          description: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          default: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          status: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          locationType: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          fax: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          timeZone: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          email: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          phone: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'address.country': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'address.subdivision': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'address.city': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'address.postalCode': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'address.streetAddress.number': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'address.streetAddress.name': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'address.streetAddress.apt': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'address.formattedAddress': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'address.hint': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'address.geocode.latitude': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'address.geocode.longitude': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'businessSchedule.periods.openDay': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'businessSchedule.periods.openTime': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'businessSchedule.periods.closeDay': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'businessSchedule.periods.closeTime': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'businessSchedule.specialHourPeriod.startDate': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'businessSchedule.specialHourPeriod.endDate': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'businessSchedule.specialHourPeriod.isClosed': z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'businessSchedule.specialHourPeriod.comment': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          revision: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          archived: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z.array(z.object({})).optional(),
    })
    .catchall(z.any())
    .describe('Information about the filters, sorting, and paging.'),
  options: z
    .object({
      filterAuthorizedLocationEntities: z
        .boolean()
        .describe('Whether to filter only authorized locations')
        .optional(),
    })
    .describe('Options to use when querying locations.')
    .optional(),
});
export const QueryLocationsResponse = z.object({
  locations: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Location ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        name: z.string().describe('Location name.').max(150).optional(),
        description: z
          .string()
          .describe('Location description.')
          .max(500)
          .optional()
          .nullable(),
        default: z
          .boolean()
          .describe(
            "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
          )
          .optional(),
        status: z
          .enum(['ACTIVE', 'INACTIVE'])
          .describe(
            "Location status. Defaults to `ACTIVE`.\n__Notes:__\n- [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect the location's status.\n- `INACTIVE` status is currently not supported."
          )
          .optional(),
        locationType: z
          .enum([
            'UNKNOWN',
            'BRANCH',
            'OFFICES',
            'RECEPTION',
            'HEADQUARTERS',
            'INVENTORY',
          ])
          .describe('Location type.\n\n**Note:** Currently not supported.')
          .optional(),
        fax: z.string().describe('Fax number.').optional().nullable(),
        timeZone: z
          .string()
          .describe('Timezone in `America/New_York` format.')
          .optional()
          .nullable(),
        email: z.string().describe('Email address.').optional().nullable(),
        phone: z.string().describe('Phone number.').optional().nullable(),
        address: z
          .object({
            streetAddress: z
              .object({
                name: z.string().optional(),
                number: z.string().optional(),
              })
              .optional(),
            city: z.string().optional().nullable(),
            subdivision: z.string().optional().nullable(),
            country: z.string().optional().nullable(),
            postalCode: z.string().optional().nullable(),
          })
          .describe('Address.')
          .optional(),
        businessSchedule: z
          .object({
            periods: z
              .array(
                z.object({
                  openDay: z
                    .enum([
                      'MONDAY',
                      'TUESDAY',
                      'WEDNESDAY',
                      'THURSDAY',
                      'FRIDAY',
                      'SATURDAY',
                      'SUNDAY',
                    ])
                    .describe('Day of the week the period starts on.')
                    .optional(),
                  openTime: z
                    .string()
                    .describe(
                      'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
                    )
                    .optional(),
                  closeDay: z
                    .enum([
                      'MONDAY',
                      'TUESDAY',
                      'WEDNESDAY',
                      'THURSDAY',
                      'FRIDAY',
                      'SATURDAY',
                      'SUNDAY',
                    ])
                    .describe('Day of the week the period ends on.')
                    .optional(),
                  closeTime: z
                    .string()
                    .describe(
                      'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                    )
                    .optional(),
                })
              )
              .max(100)
              .optional(),
            specialHourPeriod: z
              .array(
                z.object({
                  startDate: z
                    .string()
                    .describe(
                      'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                    )
                    .optional(),
                  endDate: z
                    .string()
                    .describe(
                      'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                    )
                    .optional(),
                  isClosed: z
                    .boolean()
                    .describe(
                      'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
                    )
                    .optional(),
                  comment: z
                    .string()
                    .describe(
                      'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
                    )
                    .optional(),
                })
              )
              .max(100)
              .optional(),
          })
          .describe(
            'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
          )
          .optional()
          .nullable(),
        archived: z
          .boolean()
          .describe(
            "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
          )
          .optional(),
        locationTypes: z
          .array(
            z.enum([
              'UNKNOWN',
              'BRANCH',
              'OFFICES',
              'RECEPTION',
              'HEADQUARTERS',
              'INVENTORY',
            ])
          )
          .max(10)
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields for data extensions.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging info.')
    .optional(),
  authorizedLocationEntities: z.array(z.string()).min(0).max(100).optional(),
});
export const SetDefaultLocationRequest = z.object({
  _id: z
    .string()
    .describe('ID of the location to set as the default location.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const SetDefaultLocationResponse = z.object({
  location: z
    .object({
      _id: z
        .string()
        .describe('Location ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Location name.').max(150).optional(),
      description: z
        .string()
        .describe('Location description.')
        .max(500)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
        )
        .optional(),
      status: z
        .enum(['ACTIVE', 'INACTIVE'])
        .describe(
          "Location status. Defaults to `ACTIVE`.\n__Notes:__\n- [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect the location's status.\n- `INACTIVE` status is currently not supported."
        )
        .optional(),
      locationType: z
        .enum([
          'UNKNOWN',
          'BRANCH',
          'OFFICES',
          'RECEPTION',
          'HEADQUARTERS',
          'INVENTORY',
        ])
        .describe('Location type.\n\n**Note:** Currently not supported.')
        .optional(),
      fax: z.string().describe('Fax number.').optional().nullable(),
      timeZone: z
        .string()
        .describe('Timezone in `America/New_York` format.')
        .optional()
        .nullable(),
      email: z.string().describe('Email address.').optional().nullable(),
      phone: z.string().describe('Phone number.').optional().nullable(),
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      businessSchedule: z
        .object({
          periods: z
            .array(
              z.object({
                openDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period starts on.')
                  .optional(),
                openTime: z
                  .string()
                  .describe(
                    'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
                  )
                  .optional(),
                closeDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period ends on.')
                  .optional(),
                closeTime: z
                  .string()
                  .describe(
                    'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
          specialHourPeriod: z
            .array(
              z.object({
                startDate: z
                  .string()
                  .describe(
                    'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                endDate: z
                  .string()
                  .describe(
                    'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                isClosed: z
                  .boolean()
                  .describe(
                    'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
                  )
                  .optional(),
                comment: z
                  .string()
                  .describe(
                    'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
        })
        .describe(
          'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
        )
        .optional(),
      locationTypes: z
        .array(
          z.enum([
            'UNKNOWN',
            'BRANCH',
            'OFFICES',
            'RECEPTION',
            'HEADQUARTERS',
            'INVENTORY',
          ])
        )
        .max(10)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields for data extensions.')
        .optional(),
    })
    .describe('New default location.')
    .optional(),
});
export const ArchiveLocationRequest = z.object({
  _id: z
    .string()
    .describe('ID of the location to archive.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const ArchiveLocationResponse = z.object({
  location: z
    .object({
      _id: z
        .string()
        .describe('Location ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Location name.').max(150).optional(),
      description: z
        .string()
        .describe('Location description.')
        .max(500)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
        )
        .optional(),
      status: z
        .enum(['ACTIVE', 'INACTIVE'])
        .describe(
          "Location status. Defaults to `ACTIVE`.\n__Notes:__\n- [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect the location's status.\n- `INACTIVE` status is currently not supported."
        )
        .optional(),
      locationType: z
        .enum([
          'UNKNOWN',
          'BRANCH',
          'OFFICES',
          'RECEPTION',
          'HEADQUARTERS',
          'INVENTORY',
        ])
        .describe('Location type.\n\n**Note:** Currently not supported.')
        .optional(),
      fax: z.string().describe('Fax number.').optional().nullable(),
      timeZone: z
        .string()
        .describe('Timezone in `America/New_York` format.')
        .optional()
        .nullable(),
      email: z.string().describe('Email address.').optional().nullable(),
      phone: z.string().describe('Phone number.').optional().nullable(),
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      businessSchedule: z
        .object({
          periods: z
            .array(
              z.object({
                openDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period starts on.')
                  .optional(),
                openTime: z
                  .string()
                  .describe(
                    'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
                  )
                  .optional(),
                closeDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period ends on.')
                  .optional(),
                closeTime: z
                  .string()
                  .describe(
                    'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
          specialHourPeriod: z
            .array(
              z.object({
                startDate: z
                  .string()
                  .describe(
                    'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                endDate: z
                  .string()
                  .describe(
                    'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                isClosed: z
                  .boolean()
                  .describe(
                    'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
                  )
                  .optional(),
                comment: z
                  .string()
                  .describe(
                    'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
        })
        .describe(
          'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
        )
        .optional(),
      locationTypes: z
        .array(
          z.enum([
            'UNKNOWN',
            'BRANCH',
            'OFFICES',
            'RECEPTION',
            'HEADQUARTERS',
            'INVENTORY',
          ])
        )
        .max(10)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields for data extensions.')
        .optional(),
    })
    .describe('Archived location.')
    .optional(),
});
export const AddTypeToLocationRequest = z.object({
  _id: z
    .string()
    .describe('ID of the location where the type will be added.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  locationType: z.enum([
    'UNKNOWN',
    'BRANCH',
    'OFFICES',
    'RECEPTION',
    'HEADQUARTERS',
    'INVENTORY',
  ]),
});
export const AddTypeToLocationResponse = z.object({
  location: z
    .object({
      _id: z
        .string()
        .describe('Location ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Location name.').max(150).optional(),
      description: z
        .string()
        .describe('Location description.')
        .max(500)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
        )
        .optional(),
      status: z
        .enum(['ACTIVE', 'INACTIVE'])
        .describe(
          "Location status. Defaults to `ACTIVE`.\n__Notes:__\n- [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect the location's status.\n- `INACTIVE` status is currently not supported."
        )
        .optional(),
      locationType: z
        .enum([
          'UNKNOWN',
          'BRANCH',
          'OFFICES',
          'RECEPTION',
          'HEADQUARTERS',
          'INVENTORY',
        ])
        .describe('Location type.\n\n**Note:** Currently not supported.')
        .optional(),
      fax: z.string().describe('Fax number.').optional().nullable(),
      timeZone: z
        .string()
        .describe('Timezone in `America/New_York` format.')
        .optional()
        .nullable(),
      email: z.string().describe('Email address.').optional().nullable(),
      phone: z.string().describe('Phone number.').optional().nullable(),
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      businessSchedule: z
        .object({
          periods: z
            .array(
              z.object({
                openDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period starts on.')
                  .optional(),
                openTime: z
                  .string()
                  .describe(
                    'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
                  )
                  .optional(),
                closeDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period ends on.')
                  .optional(),
                closeTime: z
                  .string()
                  .describe(
                    'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
          specialHourPeriod: z
            .array(
              z.object({
                startDate: z
                  .string()
                  .describe(
                    'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                endDate: z
                  .string()
                  .describe(
                    'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                isClosed: z
                  .boolean()
                  .describe(
                    'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
                  )
                  .optional(),
                comment: z
                  .string()
                  .describe(
                    'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
        })
        .describe(
          'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
        )
        .optional(),
      locationTypes: z
        .array(
          z.enum([
            'UNKNOWN',
            'BRANCH',
            'OFFICES',
            'RECEPTION',
            'HEADQUARTERS',
            'INVENTORY',
          ])
        )
        .max(10)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields for data extensions.')
        .optional(),
    })
    .describe('Updated location.')
    .optional(),
});
export const RemoveTypeFromLocationRequest = z.object({
  _id: z
    .string()
    .describe('ID of the location where the type will be removed.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  locationType: z.enum([
    'UNKNOWN',
    'BRANCH',
    'OFFICES',
    'RECEPTION',
    'HEADQUARTERS',
    'INVENTORY',
  ]),
});
export const RemoveTypeFromLocationResponse = z.object({
  location: z
    .object({
      _id: z
        .string()
        .describe('Location ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Location name.').max(150).optional(),
      description: z
        .string()
        .describe('Location description.')
        .max(500)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
        )
        .optional(),
      status: z
        .enum(['ACTIVE', 'INACTIVE'])
        .describe(
          "Location status. Defaults to `ACTIVE`.\n__Notes:__\n- [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect the location's status.\n- `INACTIVE` status is currently not supported."
        )
        .optional(),
      locationType: z
        .enum([
          'UNKNOWN',
          'BRANCH',
          'OFFICES',
          'RECEPTION',
          'HEADQUARTERS',
          'INVENTORY',
        ])
        .describe('Location type.\n\n**Note:** Currently not supported.')
        .optional(),
      fax: z.string().describe('Fax number.').optional().nullable(),
      timeZone: z
        .string()
        .describe('Timezone in `America/New_York` format.')
        .optional()
        .nullable(),
      email: z.string().describe('Email address.').optional().nullable(),
      phone: z.string().describe('Phone number.').optional().nullable(),
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      businessSchedule: z
        .object({
          periods: z
            .array(
              z.object({
                openDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period starts on.')
                  .optional(),
                openTime: z
                  .string()
                  .describe(
                    'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
                  )
                  .optional(),
                closeDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period ends on.')
                  .optional(),
                closeTime: z
                  .string()
                  .describe(
                    'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
          specialHourPeriod: z
            .array(
              z.object({
                startDate: z
                  .string()
                  .describe(
                    'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                endDate: z
                  .string()
                  .describe(
                    'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                isClosed: z
                  .boolean()
                  .describe(
                    'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
                  )
                  .optional(),
                comment: z
                  .string()
                  .describe(
                    'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
        })
        .describe(
          'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
        )
        .optional(),
      locationTypes: z
        .array(
          z.enum([
            'UNKNOWN',
            'BRANCH',
            'OFFICES',
            'RECEPTION',
            'HEADQUARTERS',
            'INVENTORY',
          ])
        )
        .max(10)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields for data extensions.')
        .optional(),
    })
    .describe('Updated location.')
    .optional(),
});
export const AddLocationTypeRequest = z.object({
  _id: z
    .string()
    .describe('Location id to add type')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  locationType: z.enum([
    'UNKNOWN',
    'BRANCH',
    'OFFICES',
    'RECEPTION',
    'HEADQUARTERS',
    'INVENTORY',
  ]),
});
export const AddLocationTypeResponse = z.object({
  location: z
    .object({
      _id: z
        .string()
        .describe('Location ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Location name.').max(150).optional(),
      description: z
        .string()
        .describe('Location description.')
        .max(500)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
        )
        .optional(),
      status: z
        .enum(['ACTIVE', 'INACTIVE'])
        .describe(
          "Location status. Defaults to `ACTIVE`.\n__Notes:__\n- [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect the location's status.\n- `INACTIVE` status is currently not supported."
        )
        .optional(),
      locationType: z
        .enum([
          'UNKNOWN',
          'BRANCH',
          'OFFICES',
          'RECEPTION',
          'HEADQUARTERS',
          'INVENTORY',
        ])
        .describe('Location type.\n\n**Note:** Currently not supported.')
        .optional(),
      fax: z.string().describe('Fax number.').optional().nullable(),
      timeZone: z
        .string()
        .describe('Timezone in `America/New_York` format.')
        .optional()
        .nullable(),
      email: z.string().describe('Email address.').optional().nullable(),
      phone: z.string().describe('Phone number.').optional().nullable(),
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      businessSchedule: z
        .object({
          periods: z
            .array(
              z.object({
                openDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period starts on.')
                  .optional(),
                openTime: z
                  .string()
                  .describe(
                    'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
                  )
                  .optional(),
                closeDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period ends on.')
                  .optional(),
                closeTime: z
                  .string()
                  .describe(
                    'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
          specialHourPeriod: z
            .array(
              z.object({
                startDate: z
                  .string()
                  .describe(
                    'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                endDate: z
                  .string()
                  .describe(
                    'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                isClosed: z
                  .boolean()
                  .describe(
                    'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
                  )
                  .optional(),
                comment: z
                  .string()
                  .describe(
                    'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
        })
        .describe(
          'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
        )
        .optional(),
      locationTypes: z
        .array(
          z.enum([
            'UNKNOWN',
            'BRANCH',
            'OFFICES',
            'RECEPTION',
            'HEADQUARTERS',
            'INVENTORY',
          ])
        )
        .max(10)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields for data extensions.')
        .optional(),
    })
    .describe('Full location with the new type')
    .optional(),
});
export const RemoveLocationTypeRequest = z.object({
  _id: z
    .string()
    .describe('Location id to add type')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  locationType: z.enum([
    'UNKNOWN',
    'BRANCH',
    'OFFICES',
    'RECEPTION',
    'HEADQUARTERS',
    'INVENTORY',
  ]),
});
export const RemoveLocationTypeResponse = z.object({
  location: z
    .object({
      _id: z
        .string()
        .describe('Location ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      name: z.string().describe('Location name.').max(150).optional(),
      description: z
        .string()
        .describe('Location description.')
        .max(500)
        .optional()
        .nullable(),
      default: z
        .boolean()
        .describe(
          "Whether this is the default location. There can only be one default location per site. The default location can't be archived."
        )
        .optional(),
      status: z
        .enum(['ACTIVE', 'INACTIVE'])
        .describe(
          "Location status. Defaults to `ACTIVE`.\n__Notes:__\n- [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect the location's status.\n- `INACTIVE` status is currently not supported."
        )
        .optional(),
      locationType: z
        .enum([
          'UNKNOWN',
          'BRANCH',
          'OFFICES',
          'RECEPTION',
          'HEADQUARTERS',
          'INVENTORY',
        ])
        .describe('Location type.\n\n**Note:** Currently not supported.')
        .optional(),
      fax: z.string().describe('Fax number.').optional().nullable(),
      timeZone: z
        .string()
        .describe('Timezone in `America/New_York` format.')
        .optional()
        .nullable(),
      email: z.string().describe('Email address.').optional().nullable(),
      phone: z.string().describe('Phone number.').optional().nullable(),
      address: z
        .object({
          streetAddress: z
            .object({
              name: z.string().optional(),
              number: z.string().optional(),
            })
            .optional(),
          city: z.string().optional().nullable(),
          subdivision: z.string().optional().nullable(),
          country: z.string().optional().nullable(),
          postalCode: z.string().optional().nullable(),
        })
        .describe('Address.')
        .optional(),
      businessSchedule: z
        .object({
          periods: z
            .array(
              z.object({
                openDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period starts on.')
                  .optional(),
                openTime: z
                  .string()
                  .describe(
                    'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.'
                  )
                  .optional(),
                closeDay: z
                  .enum([
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                  ])
                  .describe('Day of the week the period ends on.')
                  .optional(),
                closeTime: z
                  .string()
                  .describe(
                    'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents\nmidnight at the end of the specified day.\n\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
          specialHourPeriod: z
            .array(
              z.object({
                startDate: z
                  .string()
                  .describe(
                    'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                endDate: z
                  .string()
                  .describe(
                    'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                  )
                  .optional(),
                isClosed: z
                  .boolean()
                  .describe(
                    'Whether the business is closed (or the service is not available) during the exception.\n\nDefault: `true`.'
                  )
                  .optional(),
                comment: z
                  .string()
                  .describe(
                    'Additional info about the exception. For example, "We close earlier on New Year\'s Eve."'
                  )
                  .optional(),
              })
            )
            .max(100)
            .optional(),
        })
        .describe(
          'Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.\n\n__Note:__ Not supported by Wix Bookings.'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the location is updated.\nTo prevent conflicting changes, the existing revision must be used when updating a location.'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the location is archived. Archived locations can't be updated.\n__Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)\ndoesn't affect its `status`."
        )
        .optional(),
      locationTypes: z
        .array(
          z.enum([
            'UNKNOWN',
            'BRANCH',
            'OFFICES',
            'RECEPTION',
            'HEADQUARTERS',
            'INVENTORY',
          ])
        )
        .max(10)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields for data extensions.')
        .optional(),
    })
    .describe('Full location with the new type')
    .optional(),
});
